import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../parts/modal-new";
import { STRIPE } from "../../../../constants";
import ModalFooter from "../../../../parts/modals/ModalFooter";
import Select from "../../../../parts/inputs/select";
import Coupon from "./Coupon";
import { useRillContext } from "../../../../rill/RillReact/share/base";

const { SUPPORTED_CURRENCIES } = STRIPE;

export const CreateCoupon = (props) => {
  const { t } = useTranslation("coaching-portal");
  const { locals } = useRillContext();

  const coupons = locals?.stripeCoupon?.coupons ?? [];
  const [filter, setFilter] = useState("valid");
  const [currency, setCurrency] = useState(SUPPORTED_CURRENCIES[0]);
  const [discountType, setDiscountType] = useState("percentage");
  const [duration, setDuration] = useState("once");
  const [dateLimit, setDateLimit] = useState(false);
  const [limitTimes, setLimitTimes] = useState(false);

  const filteredCoupons = useMemo(() => {
    return coupons.filter(({ valid }) => {
      if (filter === "valid") return valid;
      return !valid;
    });
  }, [coupons, filter]);

  return (
    <Modal className="coupon-modal" title={t("planManager.createCouponModal.title")}>
      <div className="coupon-modal-container">
        <aside>
          <nav role="tablist">
            <a
              href="#"
              role="button"
              aria-current={filter === "valid" ? true : undefined}
              onClick={(e) => {
                e.preventDefault();
                setFilter("valid");
              }}
            >
              {t("planManager.createCouponModal.valid")}
            </a>
            <a
              href="#"
              role="button"
              aria-current={filter === "expired" ? true : undefined}
              onClick={(e) => {
                e.preventDefault();
                setFilter("expired");
              }}
            >
              {t("planManager.createCouponModal.expired")}
            </a>
          </nav>
          {filteredCoupons.map((coupon) => {
            return <Coupon key={coupon.id} coupon={coupon} />;
          })}
        </aside>
        <form
          className="body"
          action="/actions/plan-manager/create-coupon"
          method="POST"
          id="create-coupon"
        >
          <div className="row">
            <div className="group">
              <label htmlFor="name" className="label">
                {t("planManager.createCouponModal.couponName")}
              </label>
              <input type="text" name="name" placeholder={t("planManager.createCouponModal.enterName")} />
            </div>
          </div>
          <div className="row">
            <div className="group">
              <label htmlFor="id" className="label">
                {t("planManager.createCouponModal.couponCode")}
              </label>
              <input type="text" name="id" placeholder={t("planManager.createCouponModal.couponCodePlaceholder")} />
            </div>
          </div>
          <div className="row">
            <div className="group">
              <label htmlFor="discountType">{t("planManager.createCouponModal.discountType")}</label>
              <div className="radio-group">
                <input
                  type="radio"
                  name="discountType"
                  id="percentage"
                  value="percentage"
                  checked={discountType === "percentage"}
                  onChange={() => setDiscountType("percentage")}
                />
                <label htmlFor="percentage">{t("planManager.createCouponModal.percentageDiscount")}</label>
              </div>
              <div className="radio-group">
                <input
                  type="radio"
                  name="discountType"
                  id="fixed"
                  value="fixed"
                  checked={discountType === "fixed"}
                  onChange={() => setDiscountType("fixed")}
                />
                <label htmlFor="fixed">{t("planManager.createCouponModal.fixedAmountDiscount")}</label>
              </div>
            </div>
          </div>
          <div className="row flex gap-2">
            {discountType === "percentage" ? (
              <div className="group flex-1">
                <label htmlFor="percent_off" className="label">
                  {t("planManager.createCouponModal.discountPercentage")}
                </label>
                <input
                  type="number"
                  name="percent_off"
                  placeholder={t("planManager.createCouponModal.percentPlaceholder")}
                  required
                  min={0}
                  max={100}
                />
              </div>
            ) : (
              <>
                <div className="group flex-1">
                  <label htmlFor="amount_off" className="label">
                    {t("planManager.createCouponModal.discountAmount")}
                  </label>
                  <input
                    type="number"
                    name="amount_off"
                    placeholder={t("planManager.createCouponModal.amountPlaceholder")}
                    required
                    step={0.01}
                    min={0}
                  />
                </div>
                <div className="group flex-1">
                  <label htmlFor="currency" className="label">
                    {t("planManager.createCouponModal.currency")}
                  </label>

                  <Select
                    name="currency"
                    id="currency"
                    options={SUPPORTED_CURRENCIES.map((currency) => {
                      return {
                        value: currency.toLowerCase(),
                        text: currency,
                      };
                    })}
                    value={currency}
                    onChange={(newVal) => setCurrency(newVal)}
                  />
                </div>
              </>
            )}
          </div>
          <div className="row">
            <div className="group">
              <label htmlFor="duration">{t("planManager.createCouponModal.duration")}</label>
              <Select
                name="duration"
                id="duration"
                options={[
                  { value: "once", text: t("planManager.createCouponModal.once") },
                  { value: "forever", text: t("planManager.createCouponModal.forever") },
                  { value: "repeating", text: t("planManager.createCouponModal.repeating") },
                ]}
                value={duration}
                onChange={(newVal) => setDuration(newVal)}
              />
            </div>
          </div>
          {duration === "repeating" && (
            <div className="row">
              <div className="group">
                <label htmlFor="duration_in_months" className="label">
                  {t("planManager.createCouponModal.durationInMonths")}
                </label>
                <input
                  type="number"
                  name="duration_in_months"
                  placeholder={t("planManager.createCouponModal.durationMonthsPlaceholder")}
                  required
                  min={1}
                />
              </div>
            </div>
          )}
          <div className="row">
            <div className="group mb-4">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  name="dateLimit"
                  id="dateLimit"
                  checked={dateLimit}
                  onChange={() => setDateLimit((prev) => !prev)}
                />
                <label htmlFor="dateLimit">
                  {t("planManager.createCouponModal.limitDateRange")}
                </label>
              </div>
              {dateLimit && (
                <div className="date-range mt-3">
                  <label htmlFor="redeem_by">{t("planManager.createCouponModal.expiryDate")}</label>
                  <input
                    type="datetime-local"
                    name="redeem_by"
                    id="redeem_by"
                    required
                  />
                </div>
              )}
            </div>
            <div className="group">
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  name="limitTimes"
                  id="limitTimes"
                  checked={limitTimes}
                  onChange={() => setLimitTimes((prev) => !prev)}
                />
                <label htmlFor="limitTimes">
                  {t("planManager.createCouponModal.limitRedemptions")}
                </label>
              </div>
              {limitTimes && (
                <div className="mt-3">
                  <label htmlFor="max_redemptions" className="label">
                    {t("planManager.createCouponModal.maximumRedemptions")}
                  </label>
                  <input
                    type="number"
                    name="max_redemptions"
                    placeholder={t("planManager.createCouponModal.maxRedemptionsPlaceholder")}
                    required
                    min={1}
                  />
                </div>
              )}
            </div>
          </div>
        </form>
      </div>

      <ModalFooter
        isSubmitButton
        showAltButton={false}
        onClickButton={() => {}}
        classNameButton="button save"
        formId="create-coupon"
        buttonText={t("planManager.createCouponModal.create")}
      />
    </Modal>
  );
};
